import { parseISO } from 'date-fns';
import { PropertyTypeGroup } from 'graphql.generated';
import {
    EstimateValue,
    MarketStatsFragment,
    Sale,
    SalesSummary
} from './MarketStats.types';

const getArea = (data: MarketStatsFragment) => {
    return {
        name: data.marketStats?.areaName,
        uri: data.marketStats?.areaNameUri
    };
};

const getPropertyType = (data: MarketStatsFragment) => {
    const type = (() => {
        switch (data.marketStats?.propertyTypeGroup) {
            case PropertyTypeGroup.Flat:
                return 'flat';

            case PropertyTypeGroup.House:
                return 'house';

            default:
                return 'property';
        }
    })();
    const bedroomCount = data.counts.numBedrooms;

    return {
        short: type,
        full: bedroomCount ? `${bedroomCount} bedroom ${type}` : type
    };
};

const getHistoricalEstimateOverMonth = (
    data: MarketStatsFragment,
    month: number
) =>
    data.marketStats?.historicalEstimates?.find(
        (estimate) => estimate.overMonth === month.toString()
    );

const getHistoricalSaleOverYear = (data: MarketStatsFragment, year: number) =>
    data.marketStats?.sales?.find((sale) => sale.overYear === year.toString());

const getEstimatedValue = (data: MarketStatsFragment) => {
    const estimatedValue: EstimateValue = {
        value: null,
        changeValue: null,
        changePercentage: null
    };

    const estimatedValueNow = getHistoricalEstimateOverMonth(data, 0);
    const estimatedValueOneYearAgo = getHistoricalEstimateOverMonth(data, 12);

    if (estimatedValueNow?.value) {
        estimatedValue.value = estimatedValueNow.value;
    }

    if (
        estimatedValueOneYearAgo?.changeSince &&
        estimatedValueOneYearAgo?.pctChangeSince
    ) {
        estimatedValue.changeValue = estimatedValueOneYearAgo.changeSince;
        estimatedValue.changePercentage =
            estimatedValueOneYearAgo.pctChangeSince;
    }

    return estimatedValue;
};

const getSalesSummary = (data: MarketStatsFragment) => {
    const summary: SalesSummary = {
        averageSalesPrice: null,
        propertiesSold: null
    };

    const historicalSalesLastYear = getHistoricalSaleOverYear(data, 1);

    if (historicalSalesLastYear?.mean) {
        summary.averageSalesPrice = historicalSalesLastYear.mean;
    }

    if (historicalSalesLastYear?.numSales) {
        summary.propertiesSold = historicalSalesLastYear.numSales;
    }

    return summary;
};

const getRentalEstimate = (data: MarketStatsFragment) =>
    data.marketStats?.askingPrices?.toRent?.meanValue;

const getRecentSales = (data: MarketStatsFragment, num = 3): Array<Sale> => {
    if (!data.marketStats?.recentSales?.length) {
        return [];
    }

    const recentSales: Array<Sale | null> = data.marketStats.recentSales.map(
        (sale) => {
            if (
                !sale.date ||
                !sale.price ||
                !sale.streetAddress ||
                !sale.uri ||
                !sale.propertyId
            ) {
                return null;
            }
            return {
                date: parseISO(sale.date),
                price: sale.price,
                address: sale.streetAddress || '',
                uri: `/property/${sale.uri}/${sale.propertyId}/` || ''
            };
        }
    );

    const recentSalesFiltered = recentSales.filter(
        (sale) => sale !== null
    ) as Array<Sale>;

    return recentSalesFiltered.slice(0, num) as Array<Sale>;
};

const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
    }).format(Math.round(price));
};

export {
    getArea,
    getEstimatedValue,
    getPropertyType,
    getRecentSales,
    getRentalEstimate,
    getSalesSummary,
    formatPrice
};
